/**
 * @file Dropdown.
 */

import { gsap } from 'gsap';

/**
 * Dropdown component.
 */
const vDropdown = () => {
	const focusOutEvents = new Map();
	const animating = new Map();
	/**
	 * Close a dropdown.
	 * @param {HTMLElement} dropdown - Dropdown element.
	 * @param {Function} cb - Callback function.
	 * @returns {gsap.core.Timeline|null} Timeline.
	 */
	const closeDropdown = (dropdown, cb) => {
		if (animating.get(dropdown)) return null;
		animating.set(dropdown, true);
		const wrap = dropdown.querySelector('.v-dropdown__wrap');
		const inner = dropdown.querySelector('.v-dropdown__inner');
		const title = dropdown.querySelector('.v-dropdown__title');
		const titleArrow = title.querySelector('.v-dropdown__arrow');
		const trigger = dropdown.querySelector('.v-dropdown__trigger');
		const triggerArrow = trigger.querySelector('.v-dropdown__trigger');

		dropdown.classList.remove('v-dropdown--open');
		if (focusOutEvents.has(dropdown)) {
			dropdown.removeEventListener('focusout', focusOutEvents.get(dropdown));
			focusOutEvents.delete(dropdown);
		}

		return gsap
			.timeline({
				defaults: {
					duration: 0.2,
					ease: 'sine.out',
				},
				onComplete: () => {
					animating.set(dropdown, false);
					gsap.set([wrap, inner, titleArrow, triggerArrow, title], {
						clearProps: 'all',
					});

					if (typeof cb === 'function') {
						cb();
					}
				},
			})
			.to(inner, {
				'--clip': '100%',
				duration: 0.3,
			})
			.to(
				titleArrow,
				{
					rotateX: 0,
				},
				'>-0.2',
			)
			.to(
				[title, trigger],
				{
					'--border-radius': 1,
					duration: 0.1,
				},
				'>',
			)
			.to(
				title,
				{
					opacity: 0,
				},
				'>',
			);
	};

	/**
	 * Open a dropdown.
	 * @param {HTMLElement} dropdown - Dropdown element.
	 * @returns {gsap.core.Timeline} Timeline.
	 */
	const openDropdown = (dropdown) => {
		if (animating.get(dropdown)) return null;
		animating.set(dropdown, true);
		const wrap = dropdown.querySelector('.v-dropdown__wrap');
		const inner = dropdown.querySelector('.v-dropdown__inner');
		const title = dropdown.querySelector('.v-dropdown__title');
		const trigger = dropdown.querySelector('.v-dropdown__trigger');
		const triggerArrow = trigger.querySelector('.v-dropdown__arrow');

		dropdown.classList.add('v-dropdown--open');

		gsap.set(wrap, {
			display: 'block',
			zIndex: 1000,
		});

		return gsap
			.timeline({
				defaults: {
					duration: 0.2,
					ease: 'sine.out',
				},
				onComplete: () => {
					animating.set(dropdown, false);
					wrap.focus();
					/**
					 * Close dropdown on focus out.
					 */
					const blurClose = () => {
						setTimeout(() => {
							if (!dropdown.contains(document.activeElement)) {
								closeDropdown(dropdown);
							}
						}, 0);
					};
					focusOutEvents.set(dropdown, blurClose);
					dropdown.addEventListener('focusout', blurClose);
				},
			})
			.to(triggerArrow, {
				rotateX: 180,
			})
			.to(
				[trigger, title],
				{
					'--border-radius': 0,
					duration: 0.1,
				},
				'<',
			)
			.to(
				title,
				{
					opacity: 1,
					onComplete: () => {
						gsap.set(triggerArrow, {
							clearProps: 'all',
						});
					},
				},
				'>',
			)
			.to(
				inner,
				{
					'--clip': '0%',
					duration: 0.3,
				},
				'<',
			);
	};

	document.addEventListener('click', (e) => {
		if (
			e.target.classList.contains('v-dropdown__trigger') ||
			e.target.closest('.v-dropdown__trigger')
		) {
			const dropdown = e.target.closest('.v-dropdown');
			const openDropdowns = document.querySelectorAll('.v-dropdown--open');
			if (openDropdowns.length) {
				[...openDropdowns].forEach((open, i) => {
					closeDropdown(
						open,
						i === openDropdowns.length - 1
							? () => {
									openDropdown(dropdown);
							  }
							: null,
					);
				});
			} else {
				openDropdown(dropdown);
			}
		}

		if (
			e.target.classList.contains('v-dropdown__button') ||
			e.target.closest('.v-dropdown__button')
		) {
			const dropdown = e.target.closest('.v-dropdown');
			const button = e.target.classList.contains('v-dropdown__button')
				? e.target
				: e.target.closest('.v-dropdown__button');
			const label = dropdown.querySelector('.v-dropdown__label');
			const { source, sourceType } = dropdown.dataset;
			const { value } = button.dataset || '';

			// Update labels and classes
			dropdown
				.querySelectorAll('.v-dropdown__button--selected')
				.forEach((el) => {
					el.classList.remove('v-dropdown__button--selected');
				});
			button.classList.add('v-dropdown__button--selected');
			label.innerText = value ? button.innerText : label.dataset.default;

			// Close dropdown
			closeDropdown(dropdown, () => {
				if (source && sourceType) {
					if (sourceType === 'select') {
						const field = document.querySelector(source);

						if (field) {
							field.querySelectorAll('[selected]').forEach((s) => {
								s.removeAttribute('selected');
							});
							field
								.querySelector(`[value="${value}"]`)
								.setAttribute('selected', '');
							field.value = value;
							field.dispatchEvent(new Event('change'));
						}
					} else if (sourceType === 'radio') {
						const wrap = document.querySelector(source);
						const field = wrap.querySelector(`[value="${value}"]`);
						const checked = wrap.querySelectorAll('[checked]');
						[...checked].forEach((c) => {
							c.removeAttribute('checked');
						});

						field.setAttribute('checked', '');
						field.dispatchEvent(new Event('change', { bubbles: true }));
					}
				}
			});
		}

		if (
			e.target.classList.contains('v-dropdown__title') ||
			e.target.closest('.v-dropdown__title')
		) {
			const dropdown = e.target.closest('.v-dropdown');
			closeDropdown(dropdown);
		}
	});
};

export default vDropdown;
